import { plainLedgerText } from './goal-ledger';
import { toolkitDisplayName } from './dashboard-error-kind';

/**
 * The PLAN REVIEW sheet, as data. A marketing-plan approval (`planGoalId` on the prompt, from `display.scope` =
 * `plan`) used to offer only "See the full plan", which navigated away to the goal; the owner could not tell what they
 * were approving from where they were asked. The sheet opens in place (island, Action needed, task rows) and shows
 * the plan itself: its design, its name, where it posts, its dated steps, what it costs, and Approve / Decline.
 *
 * Every field is optional on the wire (common-lib `TaskDecisionPrompt.plan*`, stamped from `Decision.display.plan*`).
 * A server that predates them sends none, and the sheet shows the question and the detail's lines as today.
 * Read LOOSELY off the prompt, so an orion-web built against a common-lib without the fields still fits.
 *
 * Pure, no React, so the mapping is unit-tested (`lib/plan-review.test.ts`).
 */

export type PlanStepKind = 'post' | 'visual' | 'other';

export interface PlanReviewStep {
  /** `YYYY-MM-DD`. */
  date: string;
  title: string;
  kind: PlanStepKind;
}

export interface PlanReviewModel {
  /** The plan's name; absent → the unnamed title. */
  name?: string;
  /** The plan's design image. */
  previewUrl?: string;
  /** The toolkit slug the plan posts to; absent → the plan makes no posts (no channel line). */
  channel?: string;
  /** The channel's display name ("Instagram"), from the connector catalog when it has the row, else the slug. */
  channelName?: string;
  /** False ONLY when the server said so; absent (unknown) reads as connected, so nobody is pushed to connect twice. */
  channelConnected: boolean;
  /** The dated steps; empty on an older server. */
  steps: PlanReviewStep[];
  /** What the plan will spend, when the server priced it. */
  credits?: number;
  /** The detail's lines, markdown stripped, for an older server that sent no steps. Empty when steps are present. */
  detailLines: string[];
}

const str = (value: unknown): string | undefined => (typeof value === 'string' && value.trim() ? value.trim() : undefined);

/** Whether a prompt is a plan approval the review sheet opens for (the `planGoalId` stamp). */
export function isPlanReview(prompt: object): boolean {
  return !!str((prompt as { planGoalId?: unknown }).planGoalId);
}

/**
 * The prompt → the sheet. `catalogName` names a toolkit from the connectors list the app already read (the server's
 * own display name), and falls back to a readable form of the slug.
 */
export function planReviewModel(prompt: object, catalogName?: (toolkit: string) => string | undefined): PlanReviewModel {
  const p = prompt as Record<string, unknown>;
  const channel = str(p.planChannel)?.toLowerCase();
  const steps = Array.isArray(p.planSteps)
    ? p.planSteps
        .map((raw): PlanReviewStep | undefined => {
          const step = raw as Record<string, unknown> | null;
          const date = str(step?.date);
          const title = str(step?.title);
          if (!date || !title || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
          const kind = step?.kind === 'post' || step?.kind === 'visual' ? step.kind : 'other';
          return { date, title, kind };
        })
        .filter((step): step is PlanReviewStep => !!step)
    : [];
  const credits = typeof p.planCredits === 'number' && Number.isFinite(p.planCredits) && p.planCredits > 0 ? Math.round(p.planCredits) : undefined;
  const detailLines =
    steps.length > 0
      ? []
      : (typeof p.detail === 'string' ? p.detail : '')
          .split('\n')
          .map((line) => plainLedgerText(line))
          .filter((line) => line.length > 0);
  return {
    ...(str(p.planName) ? { name: str(p.planName) } : {}),
    ...(str(p.planPreviewUrl) ? { previewUrl: str(p.planPreviewUrl) } : {}),
    ...(channel ? { channel, channelName: catalogName?.(channel) || toolkitDisplayName(channel) } : {}),
    channelConnected: p.planChannelConnected !== false,
    steps,
    ...(credits !== undefined ? { credits } : {}),
    detailLines,
  };
}

/** The sheet's primary button: connect the channel first when the server said it is not connected, else approve. */
export type PlanPrimaryAction = { kind: 'connectAndApprove'; toolkit: string; appName: string } | { kind: 'approve' };

export function planPrimaryAction(model: PlanReviewModel): PlanPrimaryAction {
  if (model.channel && !model.channelConnected) {
    return { kind: 'connectAndApprove', toolkit: model.channel, appName: model.channelName || toolkitDisplayName(model.channel) };
  }
  return { kind: 'approve' };
}

/** The toolkit a hand-back option CONNECTS instead of answering (`option.connect`, from `data.connect`), else undefined. */
export function connectOptionToolkit(option: object): string | undefined {
  return str((option as { connect?: unknown }).connect)?.toLowerCase();
}

/**
 * A step's day as the sheet shows it — "Nov 3" in the reader's language (+ the year when not this year). Parsed as a
 * CALENDAR day, never through `new Date('YYYY-MM-DD')`, which is UTC midnight and reads as the day before west of it.
 */
export function planStepDay(date: string, locale: string | undefined, now: Date = new Date()): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const day = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', ...(day.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}) };
  try {
    return new Intl.DateTimeFormat(locale || undefined, options).format(day);
  } catch {
    return new Intl.DateTimeFormat(undefined, options).format(day);
  }
}
